import React, { useEffect, useMemo, useState } from "react";
import { API_URL, authFetch, parseJsonResponse, responseErrorMessage } from "../../utils/auth";

import AccountCard from "./AccountCard";
import NotificationsCard from "./NotificationsCard";
import SecurityCard from "./SecurityCard";

const PAY_LABELS = {
  commission: "Commission %",
  hourly: "Hourly rate",
  salary: "Annual salary",
};

function formatDateTime(value) {
  if (!value) return "Not updated yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not updated yet";
  return date.toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function normalizePosition(position) {
  return {
    position: position.position,
    position_label: position.position_label || position.position,
    pay_type: position.pay_type || "hourly",
    pay_value: position.pay_value || "0.00",
    currently_hiring: Boolean(position.currently_hiring),
    updated_at: position.updated_at,
    effective_at: position.effective_at,
  };
}

function BusinessAvailabilityCard({ availability, onChange, saving }) {
  const accepting = Boolean(availability?.accepting_new_projects);
  return (
    <div className={`settings-card business-availability-card ${accepting ? "good" : "bad"}`}>
      <div className="settings-card-heading">
        <h3>Business Availability</h3>
        <p>Control whether public and partner project intake can create new project requests.</p>
      </div>

      <label className="settings-toggle">
        <span>
          <strong>Accepting New Projects</strong>
          <small>{accepting ? "Public intake is open." : "Public intake is paused."}</small>
        </span>
        <input type="checkbox" checked={accepting} disabled={saving} onChange={() => onChange(!accepting)} aria-label="Accepting New Projects" />
      </label>

      <p className="hint">Existing project requests, active projects, teams, and project detail pages remain available when intake is paused.</p>
      <div className="row">
        <span>Last updated</span>
        <strong>{formatDateTime(availability?.updated_at)}</strong>
      </div>
    </div>
  );
}

function PositionRow({ position, onChange, onSave, saving }) {
  const valueLabel = PAY_LABELS[position.pay_type] || "Compensation";
  return (
    <div className="position-settings-row">
      <div className="position-settings-title">
        <strong>{position.position_label}</strong>
        <small>{position.currently_hiring ? "Currently hiring" : "Applications paused"}</small>
      </div>

      <label>
        <span>Pay model</span>
        <select value={position.pay_type} onChange={(event) => onChange(position.position, { pay_type: event.target.value })}>
          <option value="commission">Commission</option>
          <option value="hourly">Hourly</option>
          <option value="salary">Salary</option>
        </select>
      </label>

      <label>
        <span>{valueLabel}</span>
        <input type="number" min="0" step="0.01" value={position.pay_value} onChange={(event) => onChange(position.position, { pay_value: event.target.value })} />
      </label>

      <label className="position-hiring-toggle">
        <span>Hiring</span>
        <input type="checkbox" checked={position.currently_hiring} onChange={() => onChange(position.position, { currently_hiring: !position.currently_hiring })} />
      </label>

      <button className="settings-action" type="button" disabled={saving} onClick={() => onSave(position)}>{saving ? "Saving..." : "Save"}</button>
    </div>
  );
}


function money(value) {
  if (value === null || value === undefined || value === "") return "Not set";
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return `$${number.toFixed(number < 1 ? 4 : 2)}`;
}

function InfrastructurePricingCard({ rates, onSave, onAdd, savingRate }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ name: "", code: "", category: "", provider: "", unit: "", client_rate: "", provider_cost: "", included_quantity: "0", minimum_charge: "0", markup_type: "fixed", markup_value: "0", effective_date: new Date().toISOString().slice(0, 10), service_ownership: "scd_managed", is_active: true, notes: "" });
  const activeDraft = Boolean(editing);

  function updateDraft(updates) {
    setDraft((current) => ({ ...current, ...updates }));
  }

  function submitEdit(event) {
    event.preventDefault();
    if (editing === "new") onAdd(draft);
    else onSave(editing, draft);
  }

  return (
    <div className="settings-card settings-wide infrastructure-pricing-card">
      <div className="settings-card-heading infrastructure-heading-row">
        <div>
          <h3>Infrastructure & Usage Pricing</h3>
          <p>Manage SCD-managed hosting, storage, email, bandwidth, API, and other usage rates.</p>
        </div>
        <button className="settings-action" type="button" onClick={() => { setEditing("new"); setDraft({ name: "", code: "", category: "", provider: "", unit: "", client_rate: "", provider_cost: "", included_quantity: "0", minimum_charge: "0", markup_type: "fixed", markup_value: "0", effective_date: new Date().toISOString().slice(0, 10), service_ownership: "scd_managed", is_active: true, notes: "" }); }}>Add Service</button>
      </div>
      <div className="infrastructure-rate-table" role="table" aria-label="Infrastructure usage pricing">
        <div className="infrastructure-rate-row header" role="row"><span>Service</span><span>Provider</span><span>Unit</span><span>SCD Rate</span><span>Included</span><span>Provider Cost</span><span>Margin</span><span>Status</span><span>Effective</span><span>Edit</span></div>
        {rates.map((rate) => (
          <div className="infrastructure-rate-row" role="row" key={rate.id}>
            <strong>{rate.name}</strong><span>{rate.provider || "SCD"}</span><span>{rate.unit}</span><span>{money(rate.client_rate)}</span><span>{rate.included_quantity}</span><span>{money(rate.provider_cost)}</span><span>{rate.gross_margin_percentage ? `${rate.gross_margin_percentage}%` : "N/A"}</span><span>{rate.is_active ? "Active" : "Inactive"}</span><span>{rate.effective_date}</span><button type="button" onClick={() => { setEditing(rate.id); setDraft(rate); }}>Edit</button>
          </div>
        ))}
      </div>
      {activeDraft && (
        <form className="infrastructure-edit-grid" onSubmit={submitEdit}>
          <label>Service name<input value={draft.name || ""} onChange={(e) => updateDraft({ name: e.target.value })} /></label>
          <label>Code<input value={draft.code || ""} onChange={(e) => updateDraft({ code: e.target.value })} disabled={editing !== "new"} /></label>
          <label>Category<input value={draft.category || ""} onChange={(e) => updateDraft({ category: e.target.value })} /></label>
          <label>Provider<input value={draft.provider || ""} onChange={(e) => updateDraft({ provider: e.target.value })} /></label>
          <label>Unit<input value={draft.unit || ""} onChange={(e) => updateDraft({ unit: e.target.value })} /></label>
          <label>SCD client rate<input type="number" step="0.000001" value={draft.client_rate || ""} onChange={(e) => updateDraft({ client_rate: e.target.value })} /></label>
          <label>Provider cost<input type="number" step="0.000001" value={draft.provider_cost || ""} onChange={(e) => updateDraft({ provider_cost: e.target.value })} /></label>
          <label>Included amount<input type="number" step="0.000001" value={draft.included_quantity || "0"} onChange={(e) => updateDraft({ included_quantity: e.target.value })} /></label>
          <label>Effective date<input type="date" value={draft.effective_date || ""} onChange={(e) => updateDraft({ effective_date: e.target.value })} /></label>
          <label>Status<select value={draft.is_active ? "active" : "inactive"} onChange={(e) => updateDraft({ is_active: e.target.value === "active" })}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
          <label>Notes<textarea value={draft.notes || ""} onChange={(e) => updateDraft({ notes: e.target.value })} /></label>
          <div className="settings-actions-row"><button className="settings-action" disabled={savingRate}>{savingRate ? "Saving..." : "Save Pricing"}</button><button type="button" onClick={() => setEditing(null)}>Cancel</button></div>
        </form>
      )}
    </div>
  );
}

function PositionsCompensationCard({ positions, onChange, onSave, savingPosition }) {
  return (
    <div className="settings-card settings-wide positions-settings-card">
      <div className="settings-card-heading">
        <h3>Positions & Compensation</h3>
        <p>Set default pay models and hiring availability for roles used by the public jobs flow.</p>
      </div>

      <div className="position-settings-list">
        {positions.map((position) => (
          <PositionRow
            key={position.position}
            position={position}
            onChange={onChange}
            onSave={onSave}
            saving={savingPosition === position.position}
          />
        ))}
      </div>
    </div>
  );
}

export default function SuperuserSettingsPage() {
  const [settings, setSettings] = useState({ business_availability: {}, positions: [], user: {} });
  const [infrastructureRates, setInfrastructureRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [savingPosition, setSavingPosition] = useState("");
  const [savingRate, setSavingRate] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    let active = true;
    async function loadSettings() {
      setLoading(true);
      setError("");
      try {
        const [res, ratesRes] = await Promise.all([authFetch(`${API_URL}/superuser/settings/`), authFetch(`${API_URL}/superuser/infrastructure-rates/`)]);
        const data = await parseJsonResponse(res);
        const ratesData = await parseJsonResponse(ratesRes);
        if (!res.ok) throw new Error(responseErrorMessage(res, data, "Unable to load company settings."));
        if (!ratesRes.ok) throw new Error(responseErrorMessage(ratesRes, ratesData, "Unable to load infrastructure pricing."));
        if (active) {
          setSettings({
            business_availability: data?.business_availability || {},
            positions: (data?.positions || []).map(normalizePosition),
            user: data?.user || {},
          });
          setInfrastructureRates(ratesData?.results || []);
        }
      } catch (err) {
        if (active) setError(err.message || "Unable to load company settings.");
      } finally {
        if (active) setLoading(false);
      }
    }
    loadSettings();
    return () => { active = false; };
  }, []);

  const positionsByName = useMemo(() => new Map(settings.positions.map((position) => [position.position, position])), [settings.positions]);

  function updatePosition(positionKey, updates) {
    setSettings((current) => ({
      ...current,
      positions: current.positions.map((position) => position.position === positionKey ? { ...position, ...updates } : position),
    }));
  }

  async function saveAvailability(nextValue) {
    setSavingAvailability(true);
    setError("");
    setStatus("");
    const previous = settings.business_availability;
    setSettings((current) => ({ ...current, business_availability: { ...current.business_availability, accepting_new_projects: nextValue } }));
    try {
      const res = await authFetch(`${API_URL}/superuser/settings/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accepting_new_projects: nextValue }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) throw new Error(responseErrorMessage(res, data, "Unable to save business availability."));
      setSettings((current) => ({ ...current, business_availability: data?.business_availability || current.business_availability }));
      setStatus("Business availability saved.");
    } catch (err) {
      setSettings((current) => ({ ...current, business_availability: previous }));
      setError(err.message || "Unable to save business availability.");
    } finally {
      setSavingAvailability(false);
    }
  }

  async function savePosition(position) {
    setSavingPosition(position.position);
    setError("");
    setStatus("");
    try {
      const res = await authFetch(`${API_URL}/superuser/settings/positions/${position.position}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pay_type: position.pay_type, pay_value: position.pay_value, currently_hiring: position.currently_hiring }),
      });
      const data = await parseJsonResponse(res);
      if (!res.ok) throw new Error(responseErrorMessage(res, data, "Unable to save position settings."));
      const normalized = normalizePosition(data);
      setSettings((current) => ({
        ...current,
        positions: current.positions.map((item) => item.position === normalized.position ? normalized : item),
      }));
      setStatus(`${normalized.position_label} saved.`);
    } catch (err) {
      const original = positionsByName.get(position.position);
      if (original) updatePosition(position.position, original);
      setError(err.message || "Unable to save position settings.");
    } finally {
      setSavingPosition("");
    }
  }



  async function saveInfrastructureRate(rateId, draft) {
    setSavingRate(true);
    setError("");
    setStatus("");
    try {
      const res = await authFetch(`${API_URL}/superuser/infrastructure-rates/${rateId}/`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
      const data = await parseJsonResponse(res);
      if (!res.ok) throw new Error(responseErrorMessage(res, data, "Unable to save infrastructure pricing."));
      setInfrastructureRates((current) => current.map((rate) => rate.id === data.id ? data : rate));
      setStatus(`${data.name} pricing saved.`);
    } catch (err) {
      setError(err.message || "Unable to save infrastructure pricing.");
    } finally {
      setSavingRate(false);
    }
  }

  async function addInfrastructureRate(draft) {
    setSavingRate(true);
    setError("");
    setStatus("");
    try {
      const res = await authFetch(`${API_URL}/superuser/infrastructure-rates/`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
      const data = await parseJsonResponse(res);
      if (!res.ok) throw new Error(responseErrorMessage(res, data, "Unable to add infrastructure service."));
      setInfrastructureRates((current) => [...current, data]);
      setStatus(`${data.name} added.`);
    } catch (err) {
      setError(err.message || "Unable to add infrastructure service.");
    } finally {
      setSavingRate(false);
    }
  }


  if (loading) return <div className="settings-loading">Loading...</div>;

  return (
    <>
      <div className="settings-header">
        <span>Settings</span>
        <h1>Company Settings</h1>
        <p>Manage SCD-wide availability, hiring compensation defaults, notification preferences, and your account controls.</p>
      </div>

      {error && <div className="settings-error">{error}</div>}
      {status && <div className="settings-success">{status}</div>}

      <div className="settings-grid superuser-settings-grid">
        <BusinessAvailabilityCard availability={settings.business_availability} onChange={saveAvailability} saving={savingAvailability} />
        <AccountCard user={settings.user} />
        <PositionsCompensationCard positions={settings.positions} onChange={updatePosition} onSave={savePosition} savingPosition={savingPosition} />
        <InfrastructurePricingCard rates={infrastructureRates} onSave={saveInfrastructureRate} onAdd={addInfrastructureRate} savingRate={savingRate} />
        <NotificationsCard />
        <SecurityCard />
      </div>
    </>
  );
}
